import { useCallback, useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { DialogAtendimentoEncerrado } from '@/features/atendimento/components/dialog-atendimento-encerrado'
import { AtualizadorOtimistaUltimosAtendimentos } from '@/features/atendimento/entities'
import { useCriarAtendimento } from '@/features/atendimento/hooks/use-criar-atendimento'
import { useEncerrarAtendimento } from '@/features/atendimento/hooks/use-encerrar-atendimento'
import type { TAtendimento } from '@/features/atendimento/interfaces'
import { useLocalStorage } from '@/shared/hooks'
import { showToast } from '@/shared/lib/sonner'

const CLIENTE_INATIVO = 'INACTIVE_CLIENT'

export const useAtendimento = () => {
  const [atendimento, setAtendimento] = useLocalStorage<TAtendimento | null>('atendimento', null)
  const { mutateAsync: criarAtendimento, isPending: isCriandoAtendimento } = useCriarAtendimento()
  const navigate = useNavigate()
  const { t } = useTranslation(['common', 'atendimento'])
  const { mutateAsync: encerrarAtendimento } = useEncerrarAtendimento()

  const handleIniciarAtendimento = useCallback(
    async (clienteId: string, onSuccess?: VoidFunction) => {
      await criarAtendimento(clienteId, {
        onSuccess: (res) => {
          setAtendimento({ ...res.data, cliente: { ...res.data.cliente, id: clienteId } })
          onSuccess?.()
        },
        onError: (error) => {
          const erroClienteInativo = error?.response?.data?.find((e) => e.code === CLIENTE_INATIVO)
          if (erroClienteInativo) {
            showToast({
              message: t('atendimento:errors.cliente_inativo'),
              variant: 'error',
            })
            return
          }

          showToast({
            message: t('errors_gerais.erro_interno'),
            variant: 'error',
          })
        },
      })
    },
    [criarAtendimento, navigate, setAtendimento],
  )

  const handleEncerrarAtendimento = useCallback(async () => {
    if (!atendimento) return
    await encerrarAtendimento(atendimento.id, {
      onSuccess: (response) => {
        const optimistic = new AtualizadorOtimistaUltimosAtendimentos()
        optimistic.atualizar(atendimento)
        DialogAtendimentoEncerrado.show({
          duracao: response?.data.duracao,
          nome: atendimento.cliente.nome,
          imagem: atendimento.cliente?.imagem,
        })

        setAtendimento(null)
        navigate('/')
      },
      onError: (error) => {
        if (error.response?.data.some((err) => err.message.includes('já está encerrado'))) {
          showToast({ message: t('atendimento:errors.ja_encerrado'), variant: 'info' })
          setAtendimento(null)
          return
        }
        showToast({ message: t('errors_gerais.erro_interno'), variant: 'error' })
      },
    })
  }, [atendimento?.id, encerrarAtendimento, navigate, setAtendimento])

  const value = useMemo(
    () => ({
      atendimento,
      atualizarAtendimento: setAtendimento,
      handleIniciarAtendimento,
      handleEncerrarAtendimento,
      isIniciandoAtendimento: isCriandoAtendimento,
      isEmAtendimento: Boolean(atendimento?.id),
    }),
    [atendimento, handleIniciarAtendimento, isCriandoAtendimento],
  )
  return value
}
